import type { Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';

export interface ChargesByTypeRow {
  /** Stable row key. */
  key: string;
  /** Translated category label. */
  label: string;
  amount: number;
}

export interface ChargesByTypeCardProps {
  /** Card header (translated copy). */
  title: string;
  rows: ChargesByTypeRow[];
  /** The value the rows tie to — shown as the Total row and the % denominator. */
  total: number;
  /** Translated "Total" label. */
  totalLabel: string;
  /** Money formatter (currency-aware) supplied by the caller. */
  formatMoney: (amount: number) => string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * The invoice "Charges by type" breakdown — one row per category (label · bar ·
 * % of total · amount) plus a bold Total row that ties to `total`. Port of the
 * prototype `InvoiceDetail` breakdown card on MUI + `theme.ts` tokens. Purely
 * presentational; the caller maps the four `chargesByType` amounts to `rows`.
 */
export function ChargesByTypeCard({
  title,
  rows,
  total,
  totalLabel,
  formatMoney,
  ref,
}: ChargesByTypeCardProps) {
  return (
    <Card ref={ref} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {title}
      </Box>
      <Box sx={{ py: 0.75, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {rows.map((r) => {
          const pct = total > 0 ? (r.amount / total) * 100 : 0;
          return (
            <Box
              key={r.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.1 }}
            >
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 500 }}>
                {r.label}
              </Typography>
              <Box
                aria-hidden
                sx={{
                  width: 90,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: `${Math.max(0, Math.min(100, pct))}%`,
                    height: '100%',
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  width: 48,
                  textAlign: 'right',
                  fontSize: 12,
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {pct.toFixed(1)}%
              </Typography>
              <Typography
                sx={{
                  width: 110,
                  textAlign: 'right',
                  fontSize: 13.5,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatMoney(r.amount)}
              </Typography>
            </Box>
          );
        })}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.25,
            py: 1.4,
            mt: 'auto',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 700 }}>{totalLabel}</Typography>
          <Typography
            sx={{
              width: 110,
              textAlign: 'right',
              fontSize: 14,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatMoney(total)}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
